import type { ReactNode } from 'react'
import { Box, CircularProgress, Typography } from '@mui/material'
import { Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom'
import { BeoflowButton } from '@/components/BeoflowButton'
import { PaidGraceBanner } from '@/components/subscription/PaidGraceBanner'
import { isMobileWebViewAuthSession, useAuth } from '@/contexts/AuthContext'
import { purchasesBlocked } from '@/lib/platform'
import { useApprovals } from '@/services/api/approvals'
import { useSubscription } from '@/services/api/settings'
import { spacing } from '@/theme/muiTheme'

const SEAT_FREEZE_ALLOWED_PATHS = [
  '/paywall',
  '/settings',
  '/settings/sales',
  '/access',
  '/settings/billing/access-frozen',
]

const PAYMENT_GATE_ALLOWED_PATHS = new Set([
  '/paywall',
  '/settings',
  // A Settings sub-page about money ALREADY earned: a coach whose seat is
  // frozen must still be able to read what Stripe owes them, and a paywall
  // between Settings and Sales would be a surprising place to meet one.
  '/settings/sales',
  '/access',
  '/settings/billing/access-frozen',
  '/integrations',
  '/coach/payouts/connect',
  '/coach/payouts/connected',
])

/** `/inbox` and every focused item under it are exempt from both gates: an
 *  athlete with an unclaimed invite has to reach the page that answers it. */
function isInboxPath(pathname: string): boolean {
  return pathname === '/inbox' || pathname.startsWith('/inbox/')
}

const INVITE_PATH = /^\/(?:invite|team-invite|plan-invite|r\/consent-invite|r\/team-invite|r\/plan-editor-invite)\/[^/]+\/?$/
const CONSENT_INVITE_PATH = /^\/(?:invite|r\/consent-invite)\/[^/]+\/?$/

function isPaymentRecoveryPath(pathname: string): boolean {
  return pathname === '/paywall'
}

function isPaymentGateExemptPath(pathname: string): boolean {
  return PAYMENT_GATE_ALLOWED_PATHS.has(pathname) || isInboxPath(pathname) || INVITE_PATH.test(pathname)
}

function isConsentInvitePath(pathname: string): boolean {
  return CONSENT_INVITE_PATH.test(pathname)
}

/**
 * Support staff are funded by the team they join, so an assistant account that
 * no coach has added yet reads as unpaid. The server also lets in one an
 * athlete has approved sharing with through a coach's introduction. A paywall there would ask them to buy
 * something they should never pay for; what they are actually waiting on is an
 * invite.
 */
function SupportStaffWaiting({
  email,
  waitingCount,
  onSignOut,
  onOpenInbox,
}: {
  email: string
  waitingCount: number
  onSignOut: () => void
  onOpenInbox: () => void
}) {
  return (
    <Box sx={{ display: 'grid', placeItems: 'center', minHeight: '100dvh', bgcolor: 'background.default', p: spacing.lg }}>
      <Box sx={{ maxWidth: 420, textAlign: 'center' }}>
        <Typography variant="h5" sx={{ mb: spacing.md }}>Waiting for access</Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: spacing.lg }}>
          Ask your coach to invite {email} to their team. You accept it in your Inbox, and
          once you accept you go straight into the app. If a coach asked an athlete to share
          with you, you get in as soon as the athlete approves.
        </Typography>
        {waitingCount > 0 && (
          <>
            <Typography variant="body1" sx={{ mb: spacing.md }}>
              You have {waitingCount === 1 ? '1 invite waiting' : `${waitingCount} invites waiting`}.
            </Typography>
            <BeoflowButton variant="primary" size="small" onClick={onOpenInbox} sx={{ mb: spacing.lg }}>
              Open Inbox
            </BeoflowButton>
          </>
        )}
        <BeoflowButton variant="outline" size="small" onClick={onSignOut}>Sign out</BeoflowButton>
      </Box>
    </Box>
  )
}

function GateLoading() {
  return (
    <Box sx={{ display: 'grid', placeItems: 'center', minHeight: '100dvh', bgcolor: 'background.default' }}>
      <CircularProgress size={32} color="info" />
    </Box>
  )
}

export function AuthenticatedAccessGate({ children }: { children?: ReactNode }) {
  const { user, profileStatus, logout } = useAuth()
  const subscription = useSubscription()
  const location = useLocation()
  const navigate = useNavigate()
  const nativeShell = isMobileWebViewAuthSession() || purchasesBlocked()
  const recoveryPath = isPaymentRecoveryPath(location.pathname)
  const paymentGateExempt = isPaymentGateExemptPath(location.pathname)
  const coachAccount = user?.account_type === 'professional-monitor'
  // The backend owns provider verification and is the only source of truth for
  // hard-gating. Missing decisions and refresh errors fail open; native shells
  // continue to own their store-compliant paywall.
  const requiresWebPaywall =
    !subscription.isError &&
    subscription.data?.requires_web_paywall === true
  // What waits on THIS person is what the Waiting screen counts. Only that
  // screen reads it, so only the account it renders for asks for it: an
  // athlete, a paying coach and a native shell were all fetching a list
  // nothing on the page shows. (The gate already lets /access through
  // unconditionally — it is in PAYMENT_GATE_ALLOWED_PATHS — which is what
  // stops the onboarding redirect, the paywall and the waiting screen from
  // hiding a pending invite; nothing here needs to re-derive that.)
  const approvals = useApprovals({
    enabled:
      Boolean(user) &&
      user?.account_type === 'assistant' &&
      !nativeShell &&
      requiresWebPaywall &&
      !paymentGateExempt,
  })
  const waitingApprovals = approvals.data?.count ?? 0
  // Hoisted above the onboarding redirect because that redirect runs FIRST: a
  // seat-frozen coach sent into onboarding would skip the freeze remediation
  // and could create athlete invites from inside the flow.
  const seatFrozen = Boolean(
    coachAccount && !subscription.data?.has_full_access && subscription.data?.seat_access_frozen,
  )

  // Native apps own their store-compliant personal paywall. Athlete surfaces
  // do not wait on Stripe status, while coaches still wait long enough to
  // enforce the independent seat-access freeze below.
  if (
    (subscription.isLoading && !subscription.data && (!nativeShell || coachAccount)) ||
    (!user && profileStatus === 'pending')
  ) {
    return <GateLoading />
  }

  // Registration enters this same server-backed onboarding flow directly.
  // Apply the persisted flag on later web sessions too, so signing back in
  // cannot bypass unfinished onboarding. Native shells own their own flow, and
  // recovery/invite routes must remain reachable.
  //
  // Coaches are included since the coach arc exists: until then this was scoped
  // to athletes because /onboarding declined coaches outright. /onboarding/* is
  // mounted OUTSIDE this gate, so it needs no exemption path here.
  if (
    !nativeShell &&
    (user?.account_type === 'user-client' || coachAccount) &&
    user?.is_onboarding_completed === false &&
    !seatFrozen &&
    !paymentGateExempt
  ) {
    return <Navigate to="/onboarding" replace />
  }

  // The backend owns provider verification and is the only source of truth for
  // hard-gating. Missing decisions and refresh errors fail open; native shells
  // continue to own their store-compliant paywall.

  if (!nativeShell && requiresWebPaywall && !paymentGateExempt && user?.account_type === 'assistant') {
    return (
      <SupportStaffWaiting
        email={user.email}
        waitingCount={waitingApprovals}
        onSignOut={logout}
        onOpenInbox={() => navigate('/inbox')}
      />
    )
  }

  if (!nativeShell && requiresWebPaywall && !paymentGateExempt) {
    const returnTo = `${location.pathname}${location.search}`
    return (
      <Navigate
        to={`/paywall?returnTo=${encodeURIComponent(returnTo)}`}
        replace
      />
    )
  }

  if (
    seatFrozen &&
    !isConsentInvitePath(location.pathname) &&
    !isInboxPath(location.pathname) &&
    !SEAT_FREEZE_ALLOWED_PATHS.includes(location.pathname)
  ) {
    return <Navigate to="/settings/billing/access-frozen" replace />
  }

  const content = children ? <>{children}</> : <Outlet />
  return (
    <>
      {!nativeShell && !recoveryPath && <PaidGraceBanner />}
      {content}
    </>
  )
}
